import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Real Supabase Auth login screen — single-step Email + Password, matching
 * the target B2B SaaS product flow (LOGIN: Email, Password, Masuk, Lupa
 * Password; SECONDARY: Belum punya akun? / Jadwalkan Demo). Uses the same
 * real `useAuth().signIn()` call (`supabase.auth.signInWithPassword`) as
 * before — this pass only changes the step layout, not the auth mechanism.
 * Never falls back to a demo/mock login; a Supabase/network error surfaces
 * as the real error message.
 *
 * Deliberately does NOT offer self-service tenant creation — "Belum punya
 * akun?" leads to a lead-capture flow (/jadwalkan-demo), not a signup form.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function Login() {
  const { session, signIn, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (!loading && session) {
    return <Navigate to="/app" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    const { error } = await signIn(email, password);
    setSubmitting(false);
    if (error) {
      setFormError(error);
    }
    // On success, AuthContext's onAuthStateChange updates `session` and the
    // Navigate above takes over on next render — no manual redirect here.
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--alex-font-family)', background: 'var(--alex-app-bg)' }}>
      <header
        style={{
          height: 'var(--alex-topbar-height)',
          background: 'var(--alex-sidebar-bg-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 32px',
          flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 19, letterSpacing: 0.3 }}>
          <span style={{ color: '#fff' }}>ALEX</span> <span style={{ color: 'var(--alex-primary)' }}>ERP</span>
          <span style={{ color: 'var(--alex-sidebar-text-muted)', fontWeight: 500, fontSize: 12, marginLeft: 10 }}>EL Amos Web ERP</span>
        </div>
        <a
          href="mailto:support@alex-erp.dev"
          style={{ color: 'var(--alex-sidebar-text)', fontSize: 'var(--alex-fs-body)', textDecoration: 'none', fontWeight: 500 }}
        >
          Bantuan
        </a>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative' }}>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 220,
            fontWeight: 800,
            color: 'var(--alex-text-primary)',
            opacity: 0.02,
            letterSpacing: 8,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          ALEX
        </div>
        <div className="alex-card" style={{ width: 400, padding: 40, position: 'relative' }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: 'var(--alex-text-primary)' }}>Masuk ke ALEX ERP</h1>
          <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', margin: '0 0 24px' }}>
            Gunakan akun yang terdaftar di tenant Anda.
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <label style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', fontWeight: 600 }}>
              Email
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                autoFocus
                autoComplete="username"
                style={inputStyle}
              />
            </label>
            <label style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', fontWeight: 600 }}>
              Password
              <div style={{ position: 'relative' }}>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  style={{ ...inputStyle, paddingRight: 68 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{
                    position: 'absolute',
                    right: 6,
                    top: 4,
                    background: 'none',
                    border: 'none',
                    color: 'var(--alex-text-muted)',
                    fontSize: 'var(--alex-fs-caption)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: '6px 8px',
                  }}
                >
                  {showPassword ? 'Sembunyikan' : 'Tampilkan'}
                </button>
              </div>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -8 }}>
              <Link
                to="/lupa-password"
                style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none' }}
              >
                Lupa Password?
              </Link>
            </div>

            <button type="submit" disabled={submitting} style={{ ...primaryButtonStyle, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Memproses...' : 'Masuk'}
            </button>
            {formError && (
              <p style={{ color: 'var(--alex-danger)', fontSize: 'var(--alex-fs-caption)', margin: 0 }} role="alert">
                {formError}
              </p>
            )}
          </form>

          <div
            style={{
              marginTop: 24,
              paddingTop: 20,
              borderTop: '1px solid var(--alex-border)',
              textAlign: 'center',
              fontSize: 'var(--alex-fs-caption)',
              color: 'var(--alex-text-secondary)',
            }}
          >
            Belum punya akun?{' '}
            <Link to="/jadwalkan-demo" style={{ color: 'var(--alex-primary)', fontWeight: 700, textDecoration: 'none' }}>
              Jadwalkan Demo
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  marginTop: 6,
  border: '1px solid var(--alex-border)',
  borderRadius: 'var(--alex-control-radius)',
  fontSize: 'var(--alex-fs-body)',
  color: 'var(--alex-text-primary)',
  boxSizing: 'border-box',
} as const;

const primaryButtonStyle = {
  padding: '11px 12px',
  border: 'none',
  borderRadius: 'var(--alex-control-radius)',
  background: 'var(--alex-primary)',
  color: '#fff',
  fontSize: 'var(--alex-fs-body)',
  fontWeight: 700,
  cursor: 'pointer',
  width: '100%',
} as const;
